<template>
  <segment>
    <loader
      v-if="isLoading"
      style="height: 100vh;"/>
    <!-- Answers -->
    <h3 class="ui dividing header">
      <i class="icon envelope open"/>
      <div class="content">
        {{ $t('questionnaireTab.title') }}
        <div class="sub header">
          {{ $t('questionnaireTab.subtitle') }}
        </div>
      </div>
    </h3>
    <div class="ui hidden divider"/>

    <div class="ui compact fluid three item menu">
      <!-- Pre-screening -->
      <a
        :class="prescreeningLength > 0 ? '' : 'disabled'"
        class="item"
        @click.prevent="prescreeningLength === 0 ? '' : setSelectedQuestionnaire('prescreening')">
        <span>{{ $t('questionnaireTab.prescreening') }}</span>
        <div
          :class="prescreeningLength > 0 ? 'green' : 'negative'"
          class="floating ui label">
          {{ prescreeningLength }}
        </div>
      </a>

      <!-- Recurring -->
      <div
        :id="recurringDropdown"
        :class="recurringLength > 0 ? '' : 'disabled'"
        class="ui pointing dropdown item">
        <span>{{ $t('questionnaireTab.recurring') }}</span>
        <i class="dropdown icon"/>
        <div
          :class="recurringLength > 0 ? 'green' : 'negative'"
          class="floating ui label">
          {{ recurringLength }}
        </div>
        <div class="menu">
          <div
            v-for="(answerSet, key) in foundAnswers.recurring"
            :key="key"
            class="item"
            @click.prevent="setSelectedQuestionnaire('recurring', key)">
            ({{ key }}) {{ getStringFromRecurringOrder(key) }}
          </div>
        </div>
      </div>

      <!-- Exit -->
      <a
        :class="exitLength > 0 ? '' : 'disabled'"
        class="item"
        @click.prevent="exitLength === 0 ? '' : setSelectedQuestionnaire('exit')">
        <span>{{ $t('questionnaireTab.exit') }}</span>
        <div
          :class="exitLength > 0 ? 'green' : 'negative'"
          class="floating ui label">
          {{ exitLength }}
        </div>
      </a>
    </div>

    <div class="ui divider hidden"/>

    <div
      v-if="hasCompletedAllFetches && !hasFoundAnyAnswers"
      class="ui compact warning message">
      <p>
        {{ $t('questionnaireTab.noAnswers') }}
      </p>
    </div>

    <questionnaire-display
      v-if="selectedQuestionnaire && selectedAnswers"
      :questionnaire="selectedQuestionnaire"
      :answers="selectedAnswers"/>
    <!-- </div> -->

    <!-- Exit -->
    <template v-if="exitLength === 0">
      <h3 class="ui dividing header">
        <i class="icon mail"/>
        <div class="content">
          {{ $t('questionnaireTab.sendExit.title') }}
          <div class="sub header">
            {{ $t('questionnaireTab.sendExit.subtitle') }}
          </div>
        </div>
      </h3>

      <p>{{ $t('questionnaireTab.sendExit.body.main') }}</p>
      <ol>
        <li
          v-for="(paragraph, index) in $t('questionnaireTab.sendExit.body.paragraphs')"
          :key="index">
          {{ paragraph }}
        </li>
      </ol>
      <div class="ui compact warning message">
        {{ $t('questionnaireTab.sendExit.message') }}
      </div>

      <div
        v-if="exitQuestionnaireEvents.length === 0"
        class="ui primary fluid button"
        @click.prevent="sendExitQuestionnaire">
        <span><i class="share icon"/>{{ $t('questionnaireTab.sendExit.button.send') }}</span>
      </div>

      <div
        v-if="exitQuestionnaireEvents.length > 0
          && latestExitQuestionnaireMidnightTimestamp > currentSession"
        class="ui primary fluid button disabled">
        <span><i class="share icon"/>{{ $t('questionnaireTab.sendExit.button.pending') }}</span>
      </div>

      <div
        v-if="exitQuestionnaireEvents.length > 0
          && latestExitQuestionnaireMidnightTimestamp < currentSession"
        class="ui primary fluid button"
        @click.prevent="sendExitQuestionnaire">
        <span><i class="share icon"/>{{ $t('questionnaireTab.sendExit.button.remind') }}</span>
      </div>
    </template>
  </segment>
</template>

<script>
import Loader from '@/components/Loader'
import Segment from '@/components/utility/Segment'
import QuestionnaireDisplay from '@/components/patients/QuestionnaireDisplay'
import questionnaireList from '@/questionnaires/questionnaireList'
import { ExitQuestionnaireEvent } from '@/structs/structs'
import timestampManipulation from '@/mixins/timestampManipulation'
export default {
  name: 'QuestionnaireAnswers',
  components: {
    Loader,
    Segment,
    QuestionnaireDisplay
  },
  mixins: [timestampManipulation],
  props: {
    patient: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      questionnaireList,
      isLoading: true,
      hasCompletedFetch: {
        prescreening: false,
        recurring: false,
        exit: false
      },
      selectedQuestionnaire: null,
      selectedAnswers: null,
      foundAnswers: {
        prescreening: {},
        recurring: {},
        exit: {}
      }
    }
  },
  computed: {
    exitQuestionnaireEvents () {
      return this.patient.userEvents
        .filter(event => event.type === 'exitQuestionnaire')
        .sort((a, b) => a.startTimestamp - b.startTimestamp)
    },

    latestExitQuestionnaire () {
      return this.exitQuestionnaireEvents[this.exitQuestionnaireEvents.length - 1]
    },

    latestExitQuestionnaireMidnightTimestamp () {
      return this.getMidnightTimestampFromTimestamp(this.latestExitQuestionnaire.endTimestamp)
    },

    hasCompletedAllFetches: function () {
      return Object.values(this.hasCompletedFetch).every(key => key === true)
    },

    hasFoundAnyAnswers: function () {
      return Object.entries(this.foundAnswers).every(key => key.length > 0)
    },

    prescreeningLength () {
      return Object.keys(this.foundAnswers.prescreening).length > 0 ? 1 : 0
    },
    recurringLength () {
      return Object.values(this.foundAnswers.recurring).length
    },
    recurringEntries () {
      return Object.entries(this.foundAnswers.recurring)
    },
    exitLength () {
      return Object.keys(this.foundAnswers.exit).length > 0 ? 1 : 0
    },

    currentSession () {
      return this.getMidnightTimestampFromDateObject(new Date())
    },
    recurringDropdown () {
      return 'recurring-order-list'
    }
  },
  watch: {
    hasCompletedAllFetches (newVal) {
      if (newVal) this.isLoading = false
    }
  },

  // Lifecycle Hooks
  beforeCreate () {},
  created () {},
  beforeMount () {},
  mounted () {
    this.getAnswers()

    $('#' + this.recurringDropdown)
      .dropdown()
  },
  beforeUpdate () {},
  updated () {},
  beforeDestroy () {},
  destroyed () {},
  methods: {
    sendExitQuestionnaire () {
      let language = this.patient.data.language
      let exitList = this.questionnaireList.exit
      let formId = exitList[language].id || exitList['en'].id
      let url = `https://healint.typeform.com/to/${formId}?userid=${this.patient.userId}`

      // Timestamp +5 minutes: To ensure that Braze receives information before the actual time passes
      let timestamp = this.addTimeDeltaToTimestamp(Date.now(), { minutes: 5 })

      let event = new ExitQuestionnaireEvent()
      event.startTimestamp = timestamp
      event.endTimestamp = this.addTimeDeltaToTimestamp(timestamp, { days: 7 })
      event.data.reminders = [
        {
          eventName: 'migraine_care_buddy_exit_questionnaire_' + language,
          properties: {
            url: url,
            language: language
          },
          timestamps: [timestamp]
        }
      ]

      let body = [
        event
      ]

      this.postOneUserEvent(this.patient.userId, body)
        .then(() => this.$emit('refresh'))
    },

    getPrescreeningAnswers () {
      return () => new Promise((resolve, reject) => {
        this.fetchStandardAnswers(this.patient.userId, 'migraine-care-prescreening')
          .then(
            response => {
              this.foundAnswers['prescreening'] = response
            }
          )
          .catch(() => {})
          .finally(
            () => {
              this.hasCompletedFetch['prescreening'] = true
            }
          )
      })
    },

    getRecurringAnswers () {
      // Accepts UTC Timestamp in seconds and integer
      // let excludeTimestamp = Math.floor(this.patient.data.startDate / 1000)
      let excludeTimestamp = 0
      return () => new Promise((resolve, reject) => {
        this.fetchRecurringAnswers(this.patient.userId, 'migraine-care-recurring', excludeTimestamp)
          .then(
            response => {
              this.foundAnswers['recurring'] = response
            }
          )
          .catch(() => {})
          .finally(
            () => {
              this.hasCompletedFetch['recurring'] = true
            }
          )
      })
    },

    getExitAnswers () {
      let exitList = this.questionnaireList.exit
      let formId = exitList[this.patient.data.language].id || exitList['en'].id

      return () => new Promise((resolve, reject) => {
        this.fetchStandardAnswers(this.patient.userId, formId)
          .then(
            response => {
              this.foundAnswers['exit'] = response
            }
          )
          .catch(() => {})
          .finally(
            () => {
              this.hasCompletedFetch['exit'] = true
            }
          )
      })
    },

    getAnswers () {
      if (this.$qnAxios.defaults.headers.hasOwnProperty('Authorization')) {
        Promise.all(
          [
            this.getPrescreeningAnswers(),
            this.getRecurringAnswers(),
            this.getExitAnswers()
          ].map(request => request())
        )
      } else {
        setTimeout(this.getAnswers, 500)
      }
    },

    setSelectedQuestionnaire (questionnaire, recurringOrder = null) {
      if (questionnaire === 'recurring') {
        this.selectedQuestionnaire = this.questionnaireList[questionnaire]
        this.selectedAnswers = this.foundAnswers[questionnaire][recurringOrder]
      } else if (questionnaire === 'exit') {
        this.selectedQuestionnaire = this.questionnaireList[questionnaire][this.patient.data.language]
        this.selectedAnswers = this.foundAnswers[questionnaire]
      } else {
        this.selectedQuestionnaire = this.questionnaireList[questionnaire]
        this.selectedAnswers = this.foundAnswers[questionnaire]
      }
    },

    fetchStandardAnswers (userId, formId) {
      return new Promise((resolve, reject) => {
        this.$qnAxios.get(`/answer?userid=${userId}&formid=${formId}`).then(
          ({ data }) => {
            resolve(data.answer)
          },
          ({ response }) => {
            if (response.status !== 404) {
              this.$rollbar.warning('Error in fetching standard questionnaire answers')
            }
            // eslint-disable-next-line prefer-promise-reject-errors
            reject()
          }
        )
      })
    },

    fetchRecurringAnswers (userId, formId, excludeBefore) {
      let url = `/answer/recurring?userid=${userId}&formid=${formId}&exclude_before=${excludeBefore}`

      return new Promise((resolve, reject) => {
        this.$qnAxios.get(url).then(
          ({ data }) => {
            resolve(data.answer)
          },
          ({ response }) => {
            if (response.status !== 404) {
              this.$rollbar.warning('Error in fetching recurring questionnaire answers')
            }
            // eslint-disable-next-line prefer-promise-reject-errors
            reject()
          }
        )
      })
    },

    postOneUserEvent (userId, body) {
      return new Promise((resolve, reject) => {
        this.$axios.post(`${process.env.VUE_APP_PROGRAM_URL}/programUsers/${userId}/addUserEvents`, body, this.requestConfig)
          .then(
            ({ data }) => {
              resolve(data.result)
            },
            ({ response }) => {
              this.$rollbar.error('Failed to create event(s)', response)
              reject(response)
            }
          )
      })
    },

    getStringFromRecurringOrder (recurringOrder) {
      const recurringOrderNumber = Number(recurringOrder)

      const start = (recurringOrderNumber - 1) * 30 + 1
      const end = start + 29

      return this.$t('questionnaireTab.startAndEndDays', { startDay: start, endDay: end })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
